export type SortDirection = 'asc' | 'desc';

export interface SortState<TKey extends string> {
  key: TKey;
  direction: SortDirection;
}

export function nextSort<TKey extends string>(current: SortState<TKey>, key: TKey): SortState<TKey> {
  if (current.key !== key) return { key, direction: 'asc' };
  return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

export function compareNullable(a: unknown, b: unknown) {
  const left = normalize(a);
  const right = normalize(b);

  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
}

export function sortRows<T, TKey extends string>(
  rows: T[],
  sort: SortState<TKey>,
  selectors: Record<TKey, (row: T) => unknown>
) {
  const selector = selectors[sort.key];
  return [...rows].sort((a, b) => {
    const result = compareNullable(selector(a), selector(b));
    return sort.direction === 'asc' ? result : -result;
  });
}

export function SortHeader<TKey extends string>({
  label,
  sortKey,
  sort,
  onSort,
  className = '',
}: {
  label: string;
  sortKey: TKey;
  sort: SortState<TKey>;
  onSort: (key: TKey) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`inline-flex items-center gap-1 text-left font-semibold ${className}`}
    >
      <span>{label}</span>
      <span className="w-3 text-[10px] text-slate-400" aria-hidden="true">
        {active ? (sort.direction === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );
}

function normalize(value: unknown) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const date = Date.parse(value);
    if (!Number.isNaN(date) && /\d{4}-\d{2}-\d{2}/.test(value)) return date;
  }
  return value;
}
